import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BellRing, CircleCheck, LoaderCircle, MailCheck } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { API_CONFIRM, postJson } from "@/lib/forms";
import { CONTACT_EMAIL, ROUTE_DEMO, ROUTE_WAITLIST } from "@/lib/brand";
import { STATUS_LAUNCH } from "@/lib/status";

const SIDEBAR_ITEMS = [{ id: "top", label: "Start", icon: MailCheck }];

const BTN_PRIMARY =
  "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.04] transition-all duration-300 disabled:opacity-70 disabled:pointer-events-none";
const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300";

/** Texte je Zustand. `ready` und `sending` zeigen den Button zum Bestätigen. */
const STATES = {
  ready: {
    title: "Fast geschafft.",
    text: `Ein Klick noch, dann steht ihr auf der Warteliste und erfahrt als Erste, wann MatchGate ${STATUS_LAUNCH.in} startet.`,
  },
  sending: {
    title: "Fast geschafft.",
    text: `Ein Klick noch, dann steht ihr auf der Warteliste und erfahrt als Erste, wann MatchGate ${STATUS_LAUNCH.in} startet.`,
  },
  ok: {
    title: "Ihr steht auf der Liste.",
    text: `Danke! Wir melden uns, sobald MatchGate ${STATUS_LAUNCH.in} startet. Bis dahin könnt ihr in der Demo ausprobieren, wie ein Sponsoring auf MatchGate aussieht.`,
  },
  abgelaufen: {
    title: "Der Link ist abgelaufen.",
    text: "Bestätigungslinks gelten sieben Tage. Tragt euch einfach noch einmal ein, das dauert nur eine Minute.",
  },
  ungueltig: {
    title: "Dieser Link funktioniert nicht.",
    text: "Vielleicht wurde er beim Kopieren abgeschnitten. Öffnet ihn direkt aus der E-Mail oder tragt euch noch einmal ein.",
  },
  fehler: {
    title: "Das hat nicht geklappt.",
    text: `Bitte versucht es gleich noch einmal. Klappt es weiterhin nicht, schreibt uns an ${CONTACT_EMAIL}.`,
  },
};

/**
 * Schritt 2 des Double-Opt-In. Der Link aus der Mail trägt den Token hinter
 * „#“, damit er nicht in Server-Logs landet. Bestätigt wird erst per Klick:
 * Mailprogramme und Virenscanner rufen Links oft automatisch auf und sollen
 * dabei niemanden eintragen.
 */
export default function WaitlistConfirm() {
  const [token] = useState(() =>
    typeof window !== "undefined" ? window.location.hash.slice(1) : ""
  );
  const [status, setStatus] = useState(token ? "ready" : "ungueltig");

  // Token aus der Adresszeile nehmen, damit er nicht in Lesezeichen oder
  // beim Teilen weitergegeben wird.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const confirm = async () => {
    setStatus("sending");
    const result = await postJson(API_CONFIRM, { token });
    if (result.ok) setStatus("ok");
    else if (result.error === "abgelaufen" || result.error === "ungueltig") setStatus(result.error);
    else setStatus("fehler");
  };

  const state = STATES[status];
  const Icon = status === "ok" ? CircleCheck : MailCheck;

  return (
    <PageLayout sidebarItems={SIDEBAR_ITEMS} title="Eintrag bestätigen" testId="waitlist-confirm-main">
      <section
        id="top"
        className="relative min-h-[calc(100vh-var(--header-h))] flex items-center pt-32 pb-24 overflow-hidden"
      >
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-[480px] h-[480px] rounded-full bg-brand-orange/20 blur-[150px] animate-pulseGlow" />
          <div className="absolute bottom-1/4 right-1/4 w-[480px] h-[480px] rounded-full bg-brand-blue/20 blur-[150px] animate-pulseGlow" />
        </div>

        <div className="w-full px-6 md:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-2xl text-center"
          >
            <span className="mx-auto flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.06] border border-white/10 mb-8">
              <Icon className="w-6 h-6 text-brand-orange" />
            </span>

            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-7">
              <span className="w-2 h-2 rounded-full bg-brand-orange shadow-[0_0_10px_2px_rgba(255,107,0,0.8)]" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-white/70">
                Warteliste
              </span>
            </div>

            <h1
              data-testid="waitlist-confirm-title"
              className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.98]"
            >
              {state.title}
            </h1>
            <p aria-live="polite" className="mt-7 mx-auto max-w-xl text-base md:text-lg leading-relaxed text-white/60">
              {state.text}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              {(status === "ready" || status === "sending" || status === "fehler") && (
                <button
                  type="button"
                  onClick={confirm}
                  disabled={status === "sending"}
                  data-testid="waitlist-confirm-button"
                  className={BTN_PRIMARY}
                >
                  {status === "sending"
                    ? "Wird bestätigt …"
                    : status === "fehler"
                      ? "Noch einmal versuchen"
                      : "Eintrag bestätigen"}
                  {status === "sending" ? (
                    <LoaderCircle className="w-4 h-4 animate-spin" />
                  ) : (
                    <BellRing className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                  )}
                </button>
              )}

              {status === "ok" && (
                <>
                  <Link to={ROUTE_DEMO} className={BTN_PRIMARY}>
                    Demo ansehen
                  </Link>
                  <Link to="/" className={BTN_SECONDARY}>
                    Zur Startseite
                  </Link>
                </>
              )}

              {(status === "abgelaufen" || status === "ungueltig") && (
                <Link to={ROUTE_WAITLIST} className={BTN_PRIMARY}>
                  Zur Warteliste
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
}
